// Drives map-control computation for a round and exposes the per-frame state slice.
//
// Computation is stateful across the round, so it is done once in a worker when the overlay is first
// enabled, then cached. Switching rounds or maps invalidates it.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ParsedReplay } from '../replayParser';
import { loadControlGrid, hasControlGrid, type ControlGrid } from './mapControlGrid';
import type { ControlWorkerResult } from './mapControl.worker';

const STRIDE_TICKS = 16;   // 250ms at 64Hz

export interface MapControlData {
    grid: ControlGrid;
    cells: Int32Array;
    M: number;
    /** frame index for each computed sample, ascending */
    sampleFrameIdx: Int32Array;
    states: Uint8Array;
    shares: Float32Array;
    /** states for the sample covering a given frame index */
    statesAtFrame(frameIdx: number): Uint8Array | null;
}

export interface UseMapControl {
    data: MapControlData | null;
    loading: boolean;
    error: string | null;
    supported: boolean;
}

export function useMapControl(replay: ParsedReplay | null, mapName: string, enabled: boolean, zBand?: { zMin?: number; zMax?: number }): UseMapControl {
    const [data, setData] = useState<MapControlData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const workerRef = useRef<Worker | null>(null);
    const supported = hasControlGrid(mapName);

    // round identity — recompute when the loaded round changes
    const roundKey = useMemo(
        () => (replay ? `${mapName}:${replay.header.roundNumber}:${replay.frames.length}` : ''),
        [replay, mapName],
    );
    const doneKey = useRef<string>('');

    useEffect(() => {
        setData(null);
        doneKey.current = '';
    }, [roundKey]);

    useEffect(() => {
        if (!enabled || !replay || !supported) return;
        if (doneKey.current === roundKey) return;

        let cancelled = false;
        setLoading(true);
        setError(null);

        loadControlGrid(mapName)
            .then((grid) => {
                if (cancelled) return;
                if (!grid) { setError('No control grid for this map'); setLoading(false); return; }

                const shotsByTick = new Map<number, Array<{ x: number; y: number; z?: number }>>();
                for (const ev of replay.events) {
                    if (ev.type !== 'bullet_impact') continue;
                    const f = replay.frames[Math.min(ev.frameIdx, replay.frames.length - 1)];
                    if (!f) continue;
                    const list = shotsByTick.get(f.tick);
                    // Carry the shooter's height: on a split-level map a shot fired downstairs must
                    // not stamp an active fight on the floor above at the same XY.
                    const sp = ev.shooterSlot < f.players.length ? f.players[ev.shooterSlot] : null;
                    const mark = { x: ev.shooterX, y: ev.shooterY, z: sp && sp.isAlive ? sp.z : undefined };
                    if (list) list.push(mark); else shotsByTick.set(f.tick, [mark]);
                }

                workerRef.current?.terminate();
                const worker = new Worker(new URL('./mapControl.worker.ts', import.meta.url));
                workerRef.current = worker;

                worker.onmessage = (e: MessageEvent<ControlWorkerResult | { ok: false; error: string }>) => {
                    if (cancelled) return;
                    setLoading(false);
                    if (!('ok' in e.data) || !e.data.ok) {
                        setError((e.data as { error?: string }).error || 'Map control failed');
                        return;
                    }
                    const r = e.data;
                    const cells = new Int32Array(r.cells);
                    const sampleFrameIdx = new Int32Array(r.sampleFrameIdx);
                    const states = new Uint8Array(r.states);
                    const shares = new Float32Array(r.shares);
                    const M = r.M;

                    // One stable slice object per sample. The renderer eases its shapes toward the
                    // current sample and needs to know when a genuinely NEW one arrives; a fresh
                    // subarray on every call would look like new data 60 times a second.
                    const sliceCache = new Map<number, Uint8Array>();

                    setData({
                        grid, cells, M, sampleFrameIdx, states, shares,
                        statesAtFrame(frameIdx: number) {
                            if (!sampleFrameIdx.length) return null;
                            // binary search for the last sample at or before frameIdx
                            let lo = 0, hi = sampleFrameIdx.length - 1, best = 0;
                            while (lo <= hi) {
                                const mid = (lo + hi) >> 1;
                                if (sampleFrameIdx[mid] <= frameIdx) { best = mid; lo = mid + 1; }
                                else hi = mid - 1;
                            }
                            let slice = sliceCache.get(best);
                            if (!slice) { slice = states.subarray(best * M, best * M + M); sliceCache.set(best, slice); }
                            return slice;
                        },
                    });
                    doneKey.current = roundKey;
                };
                worker.onerror = (ev) => {
                    if (cancelled) return;
                    setLoading(false);
                    setError(ev.message || 'Map control worker error');
                };

                worker.postMessage({
                    grid: {
                        G: grid.G, cell: grid.cell, cfg: grid.cfg,
                        data: grid.data.slice().buffer,
                        cellPoly: grid.cellPoly.slice().buffer,
                        pvs: grid.pvs.slice().buffer,
                        pvsStride: grid.pvsStride,
                    },
                    frames: replay.frames,
                    roster: replay.roster,
                    shotsByTick: Array.from(shotsByTick.entries()),
                    strideTicks: STRIDE_TICKS,
                    zMin: zBand?.zMin,
                    zMax: zBand?.zMax,
                });
            })
            .catch((e) => { if (!cancelled) { setError(String(e)); setLoading(false); } });

        return () => { cancelled = true; };
    }, [enabled, replay, mapName, roundKey, supported, zBand?.zMin, zBand?.zMax]);

    useEffect(() => () => { workerRef.current?.terminate(); workerRef.current = null; }, []);

    return { data, loading, error, supported };
}
